import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ClinicalStatistics {
  topDiagnoses: Array<{
    diagnosis: string;
    count: number;
  }>;
  topICDCodes: Array<{
    code: string;
    description: string;
    count: number;
  }>;
  patientsSeen: number;
  meanAge: number;
  ages: number[];
  genderBreakdown: { [key: string]: number };
  consents: {
    total: number;
    completed: number;
    incomplete: number;
  };
  referralSources: {
    total: number;
    withReferralSource: number;
    withoutReferralSource: number;
    sources: Array<{
      source: string;
      count: number;
    }>;
  };
  morbidityMortality: {
    morbidity: {
      total: number;
      breakdown: Array<{
        name: string;
        value: number;
      }>;
    };
    mortality: {
      total: number;
      breakdown: Array<{
        name: string;
        value: number;
      }>;
    };
  };
}

export interface AppointmentTypeOverview {
  appointmentTypes: Array<{
    id: string;
    type: string;
    backgroundColor: string;
    textColor: string;
    isProcedure: boolean;
    totalScheduled: number;
    avgDuration: number | null;
  }>;
  overallAvgWaitingTime: number;
  totalTimeSpentMinutes: number;
  totalCancelled: number;
  totalDidNotAttend: number;
  totalAppointments: number;
}

export interface InpatientStatistics {
  totalInpatients: number;
  totalDischarges: number;
  admissionTypes: Array<{
    admissionType: string;
    count: number;
  }>;
  totalReadmissions: number;
  avgLengthOfStay: number;
  lengthOfStayBreakdown: Array<{
    lengthOfStayCategory: string;
    count: number;
  }>;
}

export interface ProcedureStatistics {
  totalProcedures: number;
  completedProcedureConsents: number;
  procedureCompliancePercentage: number;
  onboardingConsents: {
    completed: number;
    pending: number;
    notSent: number;
    compliancePercentage: number;
  };
  procedureConsents: {
    completed: number;
    pending: number;
    notSent: number;
    compliancePercentage: number;
  };
}

export interface FinancialStatistics {
  totalInvoiceAmount: number;
  totalReceiptAmount: number;
  totalOutstandingAmount: number;
  calendarData: Array<{
    date: string;
    documentType: string;
    billingNumber: string;
    amount: number;
  }>;
}

/** Connect (medical-aid claims) roll-up. All money values are integer cents (ZAR). */
export interface ConnectFinancialTotals {
  billedCents: number;
  approvedCents: number;
  rejectedCents: number;
  patientLiableCents: number;
  paidCents: number;
  clawbackCents: number;
  outstandingCents: number;
  codeCount: number;
}

export interface ConnectFinancialDay extends ConnectFinancialTotals {
  date: string;
}

/**
 * Per (service date, patient) connect roll-up. All-zero rows with codeCount > 0
 * mean "submitted, awaiting adjudication" and are intentionally not suppressed.
 */
export interface ConnectFinancialPatientDay extends ConnectFinancialDay {
  patientId: string;
  patientName: string;
}

export interface ConnectFinancialStatistics {
  enabled: boolean;
  totals: ConnectFinancialTotals | null;
  days: ConnectFinancialDay[];
  /** Only present when requested via includePatients. */
  patientDays?: ConnectFinancialPatientDay[];
}

export interface StatisticsData {
  clinical: ClinicalStatistics;
  appointmentTypeOverview: AppointmentTypeOverview;
  inpatient: InpatientStatistics;
  procedures: ProcedureStatistics;
  financial: FinancialStatistics;
}

export interface StatisticsResponse {
  success: boolean;
  data: StatisticsData;
}

@Injectable({
  providedIn: 'root'
})
export class StatisticsService {
  private apiUrl = `${environment.API_URL}/api/statistics`;

  constructor(private http: HttpClient) { }

  getStatistics(groupId: string, startDate?: string, endDate?: string, hospitalId?: string): Observable<StatisticsResponse> {
    let params: any = { groupId };

    if (startDate) {
      params.startDate = startDate;
    }

    if (endDate) {
      params.endDate = endDate;
    }

    if (hospitalId) {
      params.hospitalId = hospitalId;
    }

    return this.http.get<StatisticsResponse>(this.apiUrl, { params });
  }

  getConnectFinancials(groupId: string, startDate?: string, endDate?: string, includePatients = false): Observable<ConnectFinancialStatistics> {
    let params: any = { groupId };

    if (startDate) {
      params.startDate = startDate;
    }

    if (endDate) {
      params.endDate = endDate;
    }

    if (includePatients) {
      params.includePatients = 'true';
    }

    return this.http.get<{ success: boolean; data: ConnectFinancialStatistics }>(`${this.apiUrl}/connect-financials`, { params })
      .pipe(map(response => response.data));
  }

  getHistoricalStatistics(groupId: string, metric: string, rangeType: string, currentRange?: { startDate: string; endDate: string }): Observable<any> {
    const params: any = { metric, rangeType, groupId };
    if (currentRange) {
      params.currentRange = JSON.stringify(currentRange);
    }
    return this.http.get<any>(`${this.apiUrl}/historical`, { params });
  }
} 